import { useCallback, useMemo, useRef, useState } from "react";
import { Tag } from "antd";
import styles from "./EditLessonForm.module.scss";
import tagStyles from "../../../unit/edit/edit-content/tags-form/TagsForm.module.scss";
import Select from "@components/select/Select";
import Spinner from "@components/spinner/Spinner";
import {
  useListGroupsQuery,
  useSllAddLessonExclusionMutation,
  useSllGetLessonExclusionsQuery,
  useSllRemoveLessonExclusionMutation,
} from "@api/graphql-codegen";
import { useToast } from "@src/hooks/useToast";
import { isExclusionGroupName } from "@utils/groupExclusions";
import { SelectOption } from "@src/types";

type Props = {
  lessonId: string;
};

function getErrorMessage(error: Error) {
  return error.message || "Something went wrong. Please try again.";
}

function GroupExclusionsSection({ lessonId }: Props) {
  const { successToast, errorToast } = useToast();
  const [removingGroupId, setRemovingGroupId] = useState<string | null>(null);
  const removingGroupRef = useRef<{
    groupId: string;
    groupName: string;
  } | null>(null);
  const addingGroupRef = useRef<string | null>(null);

  const {
    data: exclusionsData,
    loading: exclusionsLoading,
    refetch: refetchExclusions,
  } = useSllGetLessonExclusionsQuery({
    variables: { input: { id: lessonId } },
    fetchPolicy: "network-only",
  });

  const { data: groupsData, loading: groupsLoading } = useListGroupsQuery({
    fetchPolicy: "network-only",
  });

  const exclusions = exclusionsData?.sllGetLessonExclusions ?? [];

  const excludedGroupIds = useMemo(
    () => new Set(exclusions.map((exclusion) => exclusion.groupId)),
    [exclusions],
  );

  const availableGroupOptions: SelectOption[] = useMemo(() => {
    return (groupsData?.listGroups ?? [])
      .filter(
        (group): group is { id: string; name: string } =>
          !!group &&
          isExclusionGroupName(group.name) &&
          !excludedGroupIds.has(group.id),
      )
      .map((group) => ({ label: group.name, value: group.id }));
  }, [groupsData?.listGroups, excludedGroupIds]);

  const [addExclusion, { loading: addLoading }] =
    useSllAddLessonExclusionMutation({
      onCompleted: async () => {
        const groupName = addingGroupRef.current;
        successToast(
          groupName
            ? `"${groupName}" has been added to group exclusions.`
            : "Group exclusion added.",
        );
        addingGroupRef.current = null;
        await refetchExclusions();
      },
      onError: (error) => {
        addingGroupRef.current = null;
        errorToast(getErrorMessage(error));
      },
    });

  const [removeExclusion] = useSllRemoveLessonExclusionMutation({
    onCompleted: async () => {
      const groupName = removingGroupRef.current?.groupName;
      successToast(
        groupName
          ? `"${groupName}" has been removed from group exclusions.`
          : "Group exclusion removed.",
      );
      removingGroupRef.current = null;
      setRemovingGroupId(null);
      await refetchExclusions();
    },
    onError: (error) => {
      removingGroupRef.current = null;
      setRemovingGroupId(null);
      errorToast(getErrorMessage(error));
    },
  });

  const handleAddGroup = useCallback(
    (option: SelectOption) => {
      addingGroupRef.current = option.label;
      addExclusion({
        variables: {
          input: { lessonId, groupId: option.value },
        },
      });
    },
    [addExclusion, lessonId],
  );

  const handleRemove = useCallback(
    (groupId: string, groupName: string) => {
      removingGroupRef.current = { groupId, groupName };
      setRemovingGroupId(groupId);
      removeExclusion({
        variables: {
          input: { lessonId, groupId },
        },
      });
    },
    [lessonId, removeExclusion],
  );

  const isAddDisabled =
    addLoading || groupsLoading || !availableGroupOptions.length;

  return (
    <div className={styles.exclusionsSection}>
      <div className={styles.subTitle}>Group Exclusions</div>
      <p className={styles.exclusionsDescription}>
        Users in excluded groups will not see this lesson in their lesson feeds.
      </p>

      {exclusionsLoading ? (
        <Spinner width={24} height={24} />
      ) : exclusions.length === 0 ? (
        <p className={styles.exclusionsEmpty}>
          No group exclusions — this lesson is visible to all groups.
        </p>
      ) : (
        <div className={tagStyles.wrapper}>
          {exclusions.map((exclusion) => {
            const isRemoving = removingGroupId === exclusion.groupId;
            const groupName = exclusion.groupName || exclusion.groupId;

            return (
              <Tag
                key={exclusion.groupId}
                className={[
                  tagStyles.editTag,
                  styles.exclusionsTag,
                  isRemoving && styles.exclusionsTagLoading,
                  removingGroupId && styles.exclusionsTagCloseDisabled,
                ]
                  .filter(Boolean)
                  .join(" ")}
                closable
                onClose={(event) => {
                  event.preventDefault();
                  if (removingGroupId) {
                    return;
                  }
                  handleRemove(exclusion.groupId, groupName);
                }}
              >
                {groupName}
                {isRemoving ? (
                  <div
                    className={styles.exclusionsLoadingBar}
                    aria-hidden="true"
                  />
                ) : null}
              </Tag>
            );
          })}
        </div>
      )}

      <div className={styles.exclusionsAddRow}>
        <Select
          label="Add exclusion"
          placeholder={
            groupsLoading
              ? "Loading groups..."
              : availableGroupOptions.length
                ? "Select a group"
                : "No groups available to exclude"
          }
          options={availableGroupOptions}
          onChange={handleAddGroup}
          disabled={isAddDisabled || !!removingGroupId}
          loading={groupsLoading || addLoading}
        />
      </div>
    </div>
  );
}

export default GroupExclusionsSection;
